import React, { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FlyoutContext } from 'components/Flyout/FlyoutContext';
import { openModal, closeModal } from 'components/Modal';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import { getApiErrorSnackbarDescription } from 'helpers/api-error-snackbar.helpers';
import { reportError } from 'helpers/error-logging';
import Segment from 'helpers/segment';
import { selectUserInfo } from 'selectors/general';

import { createSkuItem, linkItemToSubgroup, submitSkuMenuPricing } from './SkuItems.helpers';
import { getSkuVenueSource } from './SkuVenueSource.helpers';
import { SKU_ANALYTICS_EVENTS } from './SkuAnalytics.constants';
import { getCategoryId, getItemId } from './SkuMenuData.normalizers';
import { messages } from './components/SkuAddItemForm.messages';
import { SKU_ITEM_IN_REVIEW_MODAL_NAME } from './components/SkuItemInReviewModal';
import { shouldShowInReviewModal } from './components/SkuItemInReviewModal.helpers';

const ITEM_SUBMIT_ERROR_MODAL_NAME = 'ItemSubmitErrorModal';

const useSkuItemSubmission = ({ venue, currencyCode, onSuccess }) => {
  const dispatch = useDispatch();
  const { adminUserId } = useSelector(selectUserInfo);
  const isAdminUser = !!adminUserId;
  const { closeFlyout } = useContext(FlyoutContext);
  const { venueId } = venue;
  const source = getSkuVenueSource(venue);

  const handleCreateItem = useCallback(
    async values => {
      const { itemName: name, itemDescription: description, categories, menuPrice: price, linkToMenu: link } = values;

      const result = await createSkuItem(venueId, {
        name,
        description,
        enabled: true,
        soldOut: false,
      });

      const itemId = getItemId(result?.item) || getItemId(result);

      if (!itemId) {
        throw new Error('Created SKU item response is missing item id');
      }

      Segment.event(SKU_ANALYTICS_EVENTS.ITEM_CREATED, { venueId, itemId, source });

      return { itemId, name, categories, price, link };
    },
    [venueId, source]
  );

  const handleLinkCategories = useCallback(
    async (itemId, categories) => {
      if (categories.length === 0) return;

      const linkResults = await Promise.allSettled(
        categories.map((cat, idx) => linkItemToSubgroup(venueId, getCategoryId(cat), itemId, idx))
      );
      const linkFailures = linkResults.filter(r => r.status === 'rejected');
      if (linkFailures.length > 0) {
        reportError('Some category links failed', { failures: linkFailures.map(f => f.reason) });
      }

      categories.forEach((cat, idx) => {
        if (linkResults[idx].status === 'fulfilled') {
          Segment.event(SKU_ANALYTICS_EVENTS.CATEGORY_ITEM_ADDED, {
            venueId,
            categoryId: getCategoryId(cat),
            itemId,
            source,
          });
        }
      });
    },
    [venueId, source]
  );

  const handleSubmitPricing = useCallback(
    async ({ name, price, link }) => {
      if (isAdminUser) return true;

      try {
        await submitSkuMenuPricing(venue, { itemName: name, menuPrice: price, linkToMenu: link });
        return true;
      } catch (error) {
        reportError('Could not submit menu pricing for SKU Item', error);
        onSuccess?.();
        closeFlyout();
        dispatch(closeModal());
        dispatch(
          openModal(ITEM_SUBMIT_ERROR_MODAL_NAME, {
            currencyCode,
            itemName: name,
            linkToMenu: link,
            menuPrice: price,
          })
        );
        return false;
      }
    },
    [isAdminUser, venue, onSuccess, closeFlyout, dispatch, currencyCode]
  );

  const handleSubmit = useCallback(
    async values => {
      try {
        const { itemId, name, categories, price, link } = await handleCreateItem(values);
        await handleLinkCategories(itemId, categories);

        const pricingSucceeded = await handleSubmitPricing({ name, price, link });
        if (!pricingSucceeded) return;

        onSuccess?.({ itemId, itemName: name, menuPrice: price, linkToMenu: link });
        closeFlyout();

        if (shouldShowInReviewModal()) {
          dispatch(openModal(SKU_ITEM_IN_REVIEW_MODAL_NAME, { itemName: name }));
        } else {
          dispatch(
            createSuccessSnackbar(
              messages.successAddItem,
              {
                itemName: name,
                strong: chunks => <strong>{chunks}</strong>,
              },
              { isLowStyle: true }
            )
          );
        }
      } catch (error) {
        reportError('Error creating SKU item', error);
        closeFlyout();
        dispatch(
          createErrorSnackbar(
            messages.errorAddItem,
            { strong: chunks => <strong>{chunks}</strong> },
            { isLowStyle: true },
            getApiErrorSnackbarDescription(error)
          )
        );
      }
    },
    [handleCreateItem, handleLinkCategories, handleSubmitPricing, onSuccess, closeFlyout, dispatch]
  );

  return { handleSubmit };
};

export default useSkuItemSubmission;
